import max from 'lodash-es/max';
import min from 'lodash-es/min';
import sortBy from 'lodash-es/sortBy';
import sum from 'lodash-es/sum';
import {
  PolicyRecommendation,
  Product,
  ProductLevel,
  ProductYear,
  TreeMapProduct,
} from '../../graphQL/types/shared';
import { getLinearScale } from '../../NonConstantUtils';
import { failIfValidOrNonExhaustive, newHSColorMap} from '../../Utils';
import {
  getIntervalWidener,
} from '../../viz/Utils';
import {
  ExportType, getWidendedAndNicedDomain,
  IProcessedNode, maxCircleRadius, minCircleRadius,
  xDomainWideningFactor, YAxisMeasure,
  yDomainWideningFactor,
} from '../../workerStore/feasibility/Utils';
import { PolicyDimension } from '../Utils';

export interface FetchedDatum {
  product: {
    id: TreeMapProduct['product']['id']
    level: TreeMapProduct['product']['level']
    shortName: TreeMapProduct['product']['shortName']
    longName: TreeMapProduct['product']['longName']
    code: TreeMapProduct['product']['code']
    neverShow: TreeMapProduct['product']['hideFeasibility']
    topLevelParent: {
      id: TreeMapProduct['product']['topLevelParent']['id'],
    },
  };
  pci: TreeMapProduct['pci'];
  distance: TreeMapProduct['distance'];
  opportunityGain: TreeMapProduct['opportunityGain'];
  normalizedDistance: TreeMapProduct['normalizedDistance'];
  normalizedPCI: TreeMapProduct['normalizedPci'];
  normalizedOpportunityGain: TreeMapProduct['normalizedOpportunityGain'];
  rca: TreeMapProduct['rca'];
}

export interface ProductYearDatum {
  product: {
    id: Product['id'],
  };
  globalExportValue: ProductYear['globalExportValue'];
}

export interface FeasibilityWeights {
  // These are numbers in percentage points (between 0 and 100):
  distance: number;
  complexity: number;
  opportunityGain: number;
}

export const numTopProductsToBeHighlighted = 50;

export const getWeightsForPolicyCombination =
  (recommendation: PolicyRecommendation, dimension: PolicyDimension) => {

  let result: FeasibilityWeights;
  if (dimension === PolicyDimension.LowHangingFruit) {
    result = {
      distance: 60,
      complexity: 15,
      opportunityGain: 25,
    };
  } else if (dimension === PolicyDimension.LongJump) {
    result = {
      distance: 45,
      complexity: 20,
      opportunityGain: 35,
    };
  } else if (dimension === PolicyDimension.BalancedPortfolio) {
    if (recommendation === PolicyRecommendation.StrategicBets) {
      result = {
        distance: 50,
        complexity: 15,
        opportunityGain: 35,
      };
    } else if (recommendation === PolicyRecommendation.ParsimoniousIndustrial) {
      result = {
        distance: 55,
        complexity: 20,
        opportunityGain: 25,
      };
    } else if (recommendation === PolicyRecommendation.LightTouch) {
      result = {
        distance: 60,
        complexity: 20,
        opportunityGain: 20,
      };
    } else if (recommendation === PolicyRecommendation.TechFrontier) {
      throw new Error('Technological frontier should not show feasibility chart');
    } else {
      failIfValidOrNonExhaustive(recommendation, 'Invalid policy recommendation ' + recommendation);
      // These lines will never be excuted:
      result = {distance: 0, complexity: 0, opportunityGain: 0};
    }
  } else {
    failIfValidOrNonExhaustive(dimension, 'Invalid policy dimension' + dimension);
    // These lines will never be excuted:
    result = {distance: 0, complexity: 0, opportunityGain: 0};
  }
  return result;
};

interface FilteredDatum {
  id: string;
  section: string;
  level: ProductLevel;
  shortLabel: string;
  longLabel: string;
  code: string;
  distance: number;
  opportunityGain: number;
  pci: number;
  globalExportValue: number;
  score: number;
}

const widenIntervalX = getIntervalWidener(xDomainWideningFactor);
const widenIntervalY = getIntervalWidener(yDomainWideningFactor);

interface Inputs {
  data: FetchedDatum[];
  productYearData: ProductYearDatum[];
  width: number;
  height: number;
  yAxisMeasure: YAxisMeasure;
  weights: FeasibilityWeights;
  countryECI: number;
  gdpPerCaptia: number;
  dimension: PolicyDimension;
}
const transform = (inputs: Inputs) => {
  const {
    data, width, height, yAxisMeasure, weights, countryECI, gdpPerCaptia,
    dimension, productYearData,
  } = inputs;
  const {
    distance: distanceWeight,
    complexity: complexityWeight,
    opportunityGain: opportunityGainWeight,
  } = weights;
  const filteredData: FilteredDatum[] = [];
  for (const datum of data) {
    const {
      normalizedDistance, normalizedOpportunityGain,
      normalizedPCI, rca,
      distance, opportunityGain, pci,
      product: {
        id, level, shortName, longName, code,
        neverShow,
        topLevelParent: {id: section},
      },
    } = datum;
    const productYearDatum = productYearData.find(d => d.product.id === id);
    const globalExportValue = productYearDatum && productYearDatum.globalExportValue !== null
      ? productYearDatum.globalExportValue : null;
    if (pci !== null && distance !== null && opportunityGain !== null
        && pci !== null && globalExportValue !== null &&
        normalizedDistance !== null && normalizedOpportunityGain !== null
        && normalizedPCI !== null &&
        // Only show products that the country is not exporting:
        rca !== null && rca < 1 && neverShow === false) {
      const score =
        normalizedDistance * distanceWeight +
        normalizedPCI * complexityWeight +
        normalizedOpportunityGain * opportunityGainWeight;
      const filteredDatum: FilteredDatum = {
        id, section, level,
        shortLabel: shortName,
        longLabel: longName,
        code,
        distance, opportunityGain, pci,
        globalExportValue,
        score,
      };
      filteredData.push(filteredDatum);
    }
  }
  const allDistances = filteredData.map(({distance}) => distance);
  const allPCIs = filteredData.map(({pci}) => pci);
  const allOpportunityGains = filteredData.map(({opportunityGain}) => opportunityGain);
  const allWorldTrades = filteredData.map(({globalExportValue}) => globalExportValue);

  const maxDistance = max(allDistances);
  const minDistance = min(allDistances);
  const maxPCI = max(allPCIs);
  const minPCI = min(allPCIs);
  const maxOpportunityGain = max(allOpportunityGains);
  const minOpportunityGain = min(allOpportunityGains);
  const maxWorldTrade = max(allWorldTrades);
  const minWorldTrade = min(allWorldTrades);

  if (maxWorldTrade === undefined || minWorldTrade === undefined ||
      maxDistance === undefined || minDistance === undefined ||
      maxPCI === undefined || minPCI === undefined ||
      maxOpportunityGain === undefined || minOpportunityGain === undefined) {
    throw new Error('One of these values is null. Probably the filtered data is an empty array');
  }

  let yAverage: number, yMin: number, yMax: number;
  if (yAxisMeasure === YAxisMeasure.Complexity) {
    yAverage = sum(allPCIs) / allPCIs.length;
    yMin = minPCI;
    yMax = maxPCI;
  } else if (yAxisMeasure === YAxisMeasure.OpportunityGain) {
    yAverage = sum(allOpportunityGains) / allOpportunityGains.length;
    yMin = minOpportunityGain;
    yMax = maxOpportunityGain;
  } else {
    failIfValidOrNonExhaustive(yAxisMeasure, 'Invalid y axis measure' + yAxisMeasure);
    // These lines will never be executed:
    yMin = yMax = yAverage = 0;
  }
  const [processedMinY, processedMaxY] = getWidendedAndNicedDomain(yMin, yMax, widenIntervalY);

  const [processedMinX, processedMaxX] = getWidendedAndNicedDomain(
    minDistance, maxDistance, widenIntervalX,
  );
  const xScale = getLinearScale([processedMinX, processedMaxX], [0, width]);
  const radiusScale = getLinearScale(
    [minWorldTrade, maxWorldTrade], [minCircleRadius, maxCircleRadius],
  );
  const yScale = getLinearScale([processedMinY, processedMaxY], [height, 0]);

  const filteredAndSortedByScore = sortBy(filteredData, elem => elem.score).reverse();
  const tooltipMap: Record<string, IProcessedNode> = {};
  const unsortedNodes: IProcessedNode[] = [];
  let topProductCounter = 0;
  for (const datum of filteredAndSortedByScore) {
    const {
      id, section, level, shortLabel, longLabel,
      code, distance, opportunityGain, pci, globalExportValue,
    } = datum;
    // A "top" product must be among the top 50 products in terms of score and
    // must have higher PCI than the country's ECI.
    // If the countries GDP per capita is < $6k, then there should also be a
    // "ceiling" of the ECI + 2 for LowHangingFruit & Balanced, and ECI + 2.5
    // for LongJumps. Otherwise set it to 1000 to allow for no realistic maximum
    const gdpPerCapitaCeiling = 6000;
    const ceilingRange = dimension === PolicyDimension.LongJump ? 2.5 : 2;
    const pciCeiling = gdpPerCaptia <= gdpPerCapitaCeiling ? countryECI + ceilingRange : 1000;
    let isTopProduct: boolean;
    if (pci > countryECI && topProductCounter < numTopProductsToBeHighlighted &&
        pci < pciCeiling) {
      isTopProduct = true;
      topProductCounter += 1;
    } else {
      isTopProduct = false;
    }
    let yValue: number;
    if (yAxisMeasure === YAxisMeasure.Complexity) {
      yValue = pci;
    } else if (yAxisMeasure === YAxisMeasure.OpportunityGain) {
      yValue = opportunityGain;
    } else {
      failIfValidOrNonExhaustive(yAxisMeasure, 'Invalid y axis measure' + yAxisMeasure);
      // These lines will never be executed:
      yValue = 0;
    }
    const xValue = distance;
    const color = newHSColorMap.get(section);
    if (color === undefined) {
      throw new Error('Cannot find color for section ' + section);
    }
    const out: IProcessedNode = {
      id, section, level, shortLabel, longLabel,
      code, distance, opportunityGain, pci,
      detailOverlayInfo: [],
      xValue: distance,
      yValue,
      x: xScale(xValue),
      y: yScale(yValue),
      radius: radiusScale(globalExportValue),
      active: true,
      color,
      exportStatus: {type: ExportType.WhollyExported, value: globalExportValue},
      isTopProduct,
    };
    unsortedNodes.push(out);
    tooltipMap[id] = out;

  }
  const nodes = sortBy(unsortedNodes, elem => elem.radius).reverse();
  return {
    nodes,
    yAverage,
    minYOnAxis: processedMinY,
    maxYOnAxis: processedMaxY,
    minXOnAxis: processedMinX,
    maxXOnAxis: processedMaxX,
    tooltipMap,
  };
};
export default transform;
